import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldAlert, Phone, UserCheck, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { AYVENS_STATUS_LABELS } from './AyvensStatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BG = {
  da_verificare: 'bg-red-600 text-white',
  approvato: 'bg-emerald-600 text-white',
  rifiutato_ownership: 'bg-black text-white',
};

const AlertCard = ({ item, canResolve, onAction, busy }) => {
  const [esito, setEsito] = useState('');
  const active = item.status === 'da_verificare';
  return (
    <div className={`rounded-lg p-3 space-y-2 ${BG[item.status]}`} data-testid={`ayvens-alert-${item.contact_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-semibold">{item.nome_cognome}</p>
          <p className="text-xs opacity-90 flex items-center gap-1"><Phone className="w-3 h-3" />{item.telefono || '—'}</p>
        </div>
        <span className="text-[10px] uppercase font-bold bg-white/20 rounded-full px-2 py-0.5" data-testid={`ayvens-alert-status-${item.contact_id}`}>
          {AYVENS_STATUS_LABELS[item.status]}
        </span>
      </div>
      <p className="text-xs opacity-90">
        Agente: {item.owner_name || 'Non assegnato'} · Reparto: {item.reparto_nome || '—'}
        {item.convenzione_nome && <> · Convenzione: {item.convenzione_nome}</>}
      </p>
      {item.presa_in_carico_by_name && (
        <p className="text-xs font-medium flex items-center gap-1" data-testid={`ayvens-alert-taken-${item.contact_id}`}>
          <UserCheck className="w-3 h-3" />Preso in carico da: {item.presa_in_carico_by_name}
        </p>
      )}
      {!active && item.resolved_by_name && (
        <p className="text-xs flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Risolto da {item.resolved_by_name} il {new Date(item.resolved_at).toLocaleDateString('it-IT')}</p>
      )}
      {active && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {!item.presa_in_carico_by_name && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => onAction(item, 'presa')} data-testid={`ayvens-take-${item.contact_id}`}>
              Preso in Carico
            </Button>
          )}
          {canResolve && (
            <>
              <Select value={esito} onValueChange={setEsito}>
                <SelectTrigger className="h-8 w-48 bg-white text-slate-800" data-testid={`ayvens-esito-${item.contact_id}`}>
                  <SelectValue placeholder="Esito verifica" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approvato" data-testid={`ayvens-esito-approvato-${item.contact_id}`}>Approvato</SelectItem>
                  <SelectItem value="rifiutato_ownership" data-testid={`ayvens-esito-rifiutato-${item.contact_id}`}>Rifiutato (altro operatore)</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="secondary" disabled={!esito || busy} onClick={() => onAction(item, 'risolvi', esito)} data-testid={`ayvens-resolve-${item.contact_id}`}>
                Risolto / Aggiorna Stato
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const AyvensAlertBox = () => {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState({ items: [], attivi: 0, can_resolve: false });
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ayvens/alerts`, { headers: getAuthHeader() });
      setData(res.data);
    } catch { /* box non disponibile per il ruolo */ }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const onAction = async (item, action, esito) => {
    setBusy(item.contact_id);
    try {
      const url = `${API}/contacts/${item.contact_id}/ayvens/${action === 'presa' ? 'presa-in-carico' : 'risolvi'}`;
      await axios.post(url, action === 'presa' ? {} : { status: esito }, { headers: getAuthHeader() });
      toast.success(action === 'presa' ? 'Controllo preso in carico' : 'Censimento aggiornato');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setBusy(null);
    }
  };

  if (!data.items.length) return null;
  return (
    <Card className="border-red-200" data-testid="ayvens-alert-box">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <ShieldAlert className="w-5 h-5 text-red-600" />
          Allerta Censimento Ayvens
          <span className="text-xs font-semibold bg-red-100 text-red-700 rounded-full px-2 py-0.5" data-testid="ayvens-alert-count">{data.attivi} da verificare</span>
          {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {data.items.map(item => (
          <AlertCard key={item.contact_id} item={item} canResolve={data.can_resolve} onAction={onAction} busy={busy === item.contact_id} />
        ))}
      </CardContent>
    </Card>
  );
};
